import { FAQItem } from '@/components/section/faq/faq';
import { DEFAULT_DOMAIN } from '@/lib/constants';
import {
  AnswerSchema,
  BreadcrumbListSchema,
  FAQPageSchema,
  ListItemSchema,
  QuestionSchema,
} from './types';
import { richTextToPlainText, sanitizeTextForJsonLd } from './utils';

/**
 * Generates FAQ Page structured data schema
 */
export const generateFAQStructuredData = (items: FAQItem[]): FAQPageSchema => {
  const mainEntity: QuestionSchema[] = items
    .map((item): QuestionSchema | null => {
      const questionText = sanitizeTextForJsonLd(item.title);
      const answerText = sanitizeTextForJsonLd(richTextToPlainText(item.content));

      // Skip items without valid question or answer
      if (!questionText || !answerText) {
        return null;
      }

      return {
        '@type': 'Question',
        name: questionText,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText,
        } as AnswerSchema,
      };
    })
    .filter((item): item is QuestionSchema => item !== null);

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
};

/**
 * Generic structured data generator factory
 */
export const createStructuredDataGenerator = <T>(
  type: string,
  generator: (data: T) => Record<string, unknown>,
  context: string = 'https://schema.org',
) => {
  return (data: T) => ({
    '@context': context,
    '@type': type,
    ...generator(data),
  });
};

/**
 * Generates BreadcrumbList structured data schema
 */
export const generateBreadcrumbStructuredData = (
  items: Array<{ label: string; href: string }>,
  baseUrl: string = DEFAULT_DOMAIN,
): BreadcrumbListSchema => {
  const itemListElement: ListItemSchema[] = items.map((item, index) => {
    const isLast = index === items.length - 1;
    const sanitizedName = sanitizeTextForJsonLd(item.label);

    return {
      '@type': 'ListItem',
      position: index + 1, // 1-based indexing
      name: sanitizedName,
      ...(isLast ? {} : { item: `${baseUrl}${item.href}` }), // Last item doesn't need URL
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
};

/**
 * Validates breadcrumb items before generating structured data
 */
export const validateBreadcrumbItems = (items: Array<{ label: string; href: string }>): boolean => {
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }

  return items.every((item) => {
    const hasLabel = Boolean(item.label?.trim());
    const hasHref = Boolean(item.href?.trim());
    return hasLabel && hasHref;
  });
};

/**
 * Validates FAQ items before generating structured data
 */
export const validateFAQItems = (items: FAQItem[]): boolean => {
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }

  return items.some((item) => {
    const hasQuestion = Boolean(item.title?.trim());
    const hasAnswer = Boolean(richTextToPlainText(item.content)?.trim());
    return hasQuestion && hasAnswer;
  });
};
